import { useAppSelector } from '../app/hooks';
import { selectEpisodeProgress } from '../features/podcastList/store/selectors';
import { PodcastEpisodeModel } from '../models';

import { useActivePodcastProgress } from './useActivePodcastProgress';
import { useIsEpisodeRead } from './useReadEpisodes';

type EpisodeProgress = {
  position: number;
  duration: number;
  progressRatio: number;
  /** not read and strictly between 0 and 1 */
  isInProgress: boolean;
  remainingSeconds: number;
};

/**
 * Resolves an episode's listening progress: live player progress wins while the
 * episode is the active track, otherwise the saved progress is used. Shared by
 * the play buttons and the episode screen controls so the "remaining time"
 * behaviour stays consistent.
 */
export const useEpisodeProgress = (
  episode: Pick<PodcastEpisodeModel, 'podcastId' | 'id' | 'audio'> | undefined,
  enabled = true,
): EpisodeProgress => {
  const isRead = useIsEpisodeRead(episode?.podcastId, episode?.id);
  const savedProgress = useAppSelector(
    selectEpisodeProgress(episode?.podcastId ?? '', episode?.id ?? ''),
  );
  const liveProgress = useActivePodcastProgress(episode?.audio, enabled);

  const isLive = (liveProgress?.duration ?? 0) > 0;
  const position = isLive ? liveProgress!.position : (savedProgress?.position ?? 0);
  const duration = isLive ? liveProgress!.duration : (savedProgress?.duration ?? 0);
  const progressRatio = duration > 0 ? position / duration : 0;
  const isInProgress = !isRead && progressRatio > 0 && progressRatio < 1;
  const remainingSeconds = Math.max(duration - position, 0);

  return { position, duration, progressRatio, isInProgress, remainingSeconds };
};
